import {AfterViewInit, Component, ElementRef, OnDestroy, Signal, viewChild, ViewEncapsulation} from '@angular/core';
import {AgFilterComponent} from 'ag-grid-angular';
import {IFilterParams,} from 'ag-grid-community';
import {ICustomColDef} from '../../interfaces/custom-col-def.interface';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {
  SelectElementComponent
} from '../../../form-control/dynamic-form/form-element/select-element/select-element.component';
import {MatFormField} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatListModule, MatListOption, MatSelectionList} from '@angular/material/list';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {AgFilterEventPreventionMixin} from '../ag-filter-event-prevention.mixin';

interface ICustomFilterParams extends IFilterParams {
  colDef: ICustomColDef;
  enumValues?: any[];
  valueFormatter?: (value: any) => string;
}

type TFilter = 'contains' | 'notContains'

@Component({
  selector: 'app-repository-filter',
  templateUrl: './repository-filter.component.html',
  styleUrls: ['./repository-filter.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    MatSelectionList,
    MatListOption,
    MatListModule,
    MatCheckboxModule,
  ],
})
export class RepositoryFilterComponent extends AgFilterEventPreventionMixin implements AgFilterComponent, AfterViewInit, OnDestroy {
  selectElement: Signal<SelectElementComponent> = viewChild<SelectElementComponent>(SelectElementComponent);
  enumSelectionList: Signal<MatSelectionList> = viewChild<MatSelectionList>(MatSelectionList);

  params: ICustomFilterParams;
  enumValues: string[] = [];
  selectedValues: string[] = [];
  filterType: TFilter = 'contains';
  valueFormatter: (value: any) => string;

  selectFormControl: FormControl = new FormControl([]);

  private tempFilterType: TFilter = 'contains';
  private clickedButton: HTMLButtonElement | null = null;
  private buttonMouseDownListener: ((event: MouseEvent) => void) | null = null;

  constructor(private elementRef: ElementRef) {
    super();
  }

  agInit(params: ICustomFilterParams): void {
    this.params = params;
    this.valueFormatter = params.valueFormatter || ((v) => v?.toString() || '');

    this.enumValues = params.colDef?.apiCol?.repository || params.enumValues || [];
  }

  ngAfterViewInit(): void {
    // add listeners to register button clicks through overlay of dropdown
    setTimeout((): void => {
      if (this.selectElement()?.selector) {
        this.selectElement().selector.openedChange.subscribe((opened: boolean): void => {
          if (opened) {
            this.addDocumentClickListener();
            this.addButtonListeners();
          } else {
            this.removeDocumentClickListener();
            this.removeButtonListeners();
            this.checkButtonClickOnClose();
          }
        });
      }
    }, 0);
  }

  private checkButtonClickOnClose(): void {
    setTimeout((): void => {
      if (this.clickedButton) {
        this.clickedButton.click();
        this.clickedButton = null;
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.removeDocumentClickListener();
    this.removeButtonListeners();
    this.removeOperatorSelectDocumentClickListener();
  }

  private addButtonListeners(): void {
    const buttons = this.elementRef.nativeElement.querySelectorAll('.ag-filter-apply-panel-button');
    this.buttonMouseDownListener = (event: MouseEvent): void => {
      this.clickedButton = event.currentTarget as HTMLButtonElement;
      // Prevent the default click and let us handle it after the overlay closes
      event.preventDefault();
      event.stopPropagation();
    };

    buttons.forEach((button: HTMLButtonElement): void => {
      button.addEventListener('mousedown', this.buttonMouseDownListener, {capture: true});
    });
  }

  private removeButtonListeners(): void {
    if (this.buttonMouseDownListener) {
      const buttons = this.elementRef.nativeElement.querySelectorAll('.ag-filter-apply-panel-button');
      buttons.forEach((button: HTMLButtonElement): void => {
        button.removeEventListener('mousedown', this.buttonMouseDownListener, {capture: true});
      });
      this.buttonMouseDownListener = null;
    }
  }

  private addDocumentClickListener(): void {
    this.removeDocumentClickListener();

    this.documentClickListener = (event: MouseEvent): void => {
      const target: HTMLElement = event.target as HTMLElement;

      // If clicking on the backdrop, check what element is underneath at the click position
      if (target.classList.contains('cdk-overlay-backdrop')) {
        // Temporarily hide the backdrop to find the element underneath
        const originalPointerEvents: string = (target as HTMLElement).style.pointerEvents;
        (target as HTMLElement).style.pointerEvents = 'none';
        const elementUnderCursor: Element = document.elementFromPoint(event.clientX, event.clientY);
        (target as HTMLElement).style.pointerEvents = originalPointerEvents;

        const buttonElement: HTMLButtonElement
          = elementUnderCursor?.closest('.ag-filter-apply-panel-button') as HTMLButtonElement;
        if (buttonElement) {
          this.clickedButton = buttonElement;
          return;
        }
      }

      // Check if the click is on a button before the overlay closes
      const buttonElement: HTMLButtonElement
        = target.closest('.ag-filter-apply-panel-button') as HTMLButtonElement;
      if (buttonElement) {
        this.clickedButton = buttonElement;
        return;
      }

      const isInOverlay: boolean = target.closest('.cdk-overlay-container') !== null;
      const isInCustomPanel: boolean = target.closest('.custom-panel') !== null;
      const isInMatOption: boolean = target.closest('mat-option') !== null;
      const isInCheckbox: boolean = target.closest('.select-all-checkbox') !== null;

      if (isInOverlay || isInCustomPanel || isInMatOption || isInCheckbox) {
        event.stopPropagation();
      }
    };

    this.documentKeyboardListener = (event: KeyboardEvent): void => {
      const target: HTMLElement = event.target as HTMLElement;

      const isInOverlay: boolean = target.closest('.cdk-overlay-container') !== null;
      const isInCustomPanel: boolean = target.closest('.custom-panel') !== null;
      const isInMatOption: boolean = target.closest('mat-option') !== null;
      const isInCheckbox: boolean = target.closest('.select-all-checkbox') !== null;
      const isInFilterWrapper: boolean = target.closest('.ag-filter-wrapper') !== null;
      const isMatSelect: boolean = target.tagName === 'MAT-SELECT';

      // Stop propagation if:
      // 1. We're in the overlay/custom panel (dropdown content)
      // OR
      // 2. We're on the mat-select-element itself while the dropdown is open
      if (isInOverlay || isInCustomPanel || isInMatOption || isInCheckbox || (isMatSelect && isInFilterWrapper)) {
        event.stopPropagation();
      }
    };

    document.addEventListener('mousedown', this.documentClickListener, {capture: true});
    document.addEventListener('keydown', this.documentKeyboardListener, {capture: false});
    document.addEventListener('keyup', this.documentKeyboardListener, {capture: false});
  }

  isFilterActive(): boolean {
    return this.selectedValues && this.selectedValues.length > 0;
  }

  doesFilterPass(): boolean {
    return true;
  }

  getModel(): any {
    if (!this.isFilterActive()) {
      return null;
    }

    return {
      filterType: 'select',
      type: this.filterType,
      filter: this.selectedValues.map((value: string): number => this.enumValues.indexOf(value)),
    };
  }

  setModel(model: any): void {
    if (model) {
      this.filterType = model.type || 'contains';
      this.selectedValues = Array.isArray(model.filter) ? model.filter : [];

      this.tempFilterType = this.filterType;

      this.selectFormControl.setValue(this.selectedValues, {emitEvent: false});

    } else {
      this.clearFilter();
    }
  }

  onFilterChanged(): void {
    this.params.filterChangedCallback();
  }

  applyFilter(): void {
    this.filterType = this.tempFilterType;

    this.onFilterChanged();
  }

  clearFilter(): void {
    this.selectedValues = [];
    this.enumSelectionList().deselectAll();
    this.filterType = 'contains';
    this.tempFilterType = 'contains';
    this.onFilterChanged();
  }

  onFilterTypeChange(): void {
    this.tempFilterType = this.filterType;
  }

  isSelected(value: string): boolean {
    return this.selectedValues.includes(value);
  }

  onSelectionChange() {
    this.selectedValues = this.enumSelectionList().selectedOptions.selected
      .map((val: MatListOption): string => val.value);
  }
}
